import { useCallback, useState } from 'react';
import { useDataFetchingClient, useQuery } from 'data-fetching-client';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PropertyListEditor from 'crm_components/dialog/propertyListEditor/PropertyListEditor';
import H2 from 'UIComponents/elements/headings/H2';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModal from 'UIComponents/dialog/UIModal';
import { DEAL } from 'customer-data-objects/constants/ObjectTypes';
import { COLUMN_SETTINGS } from 'customer-data-objects/property/PropertyListTypes';
import OverrideConfirmationModal from './OverrideConfirmationModal';
import { GET_PROPERTY_GROUPS } from './api/propertyGroups';
import { DEAL_TAGS_ACCESS_SCOPE, MULTIPLE_OUTCOMES_GATE } from './constants/auth';
import { TRACK_SAVE_COLUMNS_FROM_WIZARD } from '../../constants/ForecastType';
import { shouldTrack } from '../../utils/trackerUtils';
import { getPropertyGroupsAsOptions, getSelectedColumns, haveSelectedColumnsBeenUpdated } from '../../utils/dealPropertyUtils';
import { DEFAULT_COLUMNS_FIELD_NAME, GET_DEFAULT_COLUMNS } from '../data/dataFetchingQueries';
import { showSaveDefaultColumnsFailureAlert } from '../../utils/alertUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// Modal experience for editing default columns
// Optionally renders the `OverrideConfirmationModal` after saving the default columns
const EditDefaultColumnsModal = ({
  gates,
  isWeightedOrTotalPortalConfig,
  onClose,
  onSave,
  onTrack,
  persistedColumns,
  properties,
  scopes,
  selectedForecastTypeAmountProperty,
  selectedForecastTypeId,
  selectedForecastTypeName,
  useColumnOverrideConfirmationModal
}) => {
  var _propertyGroupsData$p, _propertyGroupsData$p2, _columnsData$defaultC;
  const client = useDataFetchingClient();
  const hasDealTagsAccess = scopes.includes(DEAL_TAGS_ACCESS_SCOPE);
  const isFmoEnabled = gates.includes(MULTIPLE_OUTCOMES_GATE);
  const [showOverrideConfirmationModal, setShowOverrideConfirmationModal] = useState(false);
  const {
    loading: columnsLoading,
    data: columnsData,
    error: columnsError
  } = useQuery(GET_DEFAULT_COLUMNS, {
    variables: {
      forecastTypeId: selectedForecastTypeId,
      amountProperty: selectedForecastTypeAmountProperty
    }
  });
  const {
    loading: propertyGroupsLoading,
    data: propertyGroupsData
  } = useQuery(GET_PROPERTY_GROUPS);
  const propertyGroups = (_propertyGroupsData$p = propertyGroupsData === null || propertyGroupsData === void 0 || (_propertyGroupsData$p2 = propertyGroupsData.propertyGroups) === null || _propertyGroupsData$p2 === void 0 ? void 0 : _propertyGroupsData$p2.results) !== null && _propertyGroupsData$p !== void 0 ? _propertyGroupsData$p : [];
  const columns = (_columnsData$defaultC = columnsData === null || columnsData === void 0 ? void 0 : columnsData.defaultColumns) !== null && _columnsData$defaultC !== void 0 ? _columnsData$defaultC : [];
  const [selectedColumns, setSelectedColumns] = useState(persistedColumns || null);
  const trackSaveColumnsFromWizard = useCallback(() => {
    if (shouldTrack(onTrack)) {
      onTrack === null || onTrack === void 0 || onTrack(TRACK_SAVE_COLUMNS_FROM_WIZARD);
    }
  }, [onTrack]);
  const handleUpdateSelectedColumns = useCallback(newSelectedColumns => {
    setSelectedColumns(newSelectedColumns);
  }, []);
  const handleApplySelectedColumns = useCallback(() => {
    if (!(selectedColumns !== null && selectedColumns !== void 0 && selectedColumns.length)) {
      return;
    }
    trackSaveColumnsFromWizard();
    onSave({
      forecastTypeId: selectedForecastTypeId,
      columns: selectedColumns
    }).then(saveSuccessful => {
      if (!saveSuccessful) {
        showSaveDefaultColumnsFailureAlert();
        onClose();
        return;
      }
      client.cache.evict({
        fieldName: DEFAULT_COLUMNS_FIELD_NAME
      });
      if (!useColumnOverrideConfirmationModal) {
        onClose();
        return;
      }
      setShowOverrideConfirmationModal(true);
    }).catch(() => {
      showSaveDefaultColumnsFailureAlert();
      onClose();
    });
  }, [client.cache, onClose, onSave, selectedColumns, selectedForecastTypeId, trackSaveColumnsFromWizard, useColumnOverrideConfirmationModal]);
  if (!properties || !propertyGroups || propertyGroupsLoading || !columns || columnsLoading || columnsError) {
    return null;
  }
  const propertyCategories = getPropertyGroupsAsOptions(propertyGroups, isWeightedOrTotalPortalConfig, hasDealTagsAccess);
  const isSaveColumnsDisabled = !haveSelectedColumnsBeenUpdated(selectedColumns, columns);

  // Optionally display the `OverrideConfirmationModal` after Save
  if (showOverrideConfirmationModal) {
    if (selectedForecastTypeId === null) {
      Raven.captureMessage('EditDefaultColumnsModal: selectedForecastTypeId is null when attempting to show override modal');
      return null;
    }
    return /*#__PURE__*/_jsx(OverrideConfirmationModal, {
      forecastTypeId: selectedForecastTypeId,
      onClose: onClose,
      onTrack: onTrack
    });
  }
  return /*#__PURE__*/_jsxs(UIModal, {
    width: 1000,
    children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(H2, {
        children: isFmoEnabled && selectedForecastTypeName ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "editDefaultColumnsModal.titleWithForecast",
          options: {
            forecastName: selectedForecastTypeName
          }
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "editDefaultColumnsModal.title"
        })
      })]
    }), /*#__PURE__*/_jsx(UIDialogBody, {
      children: /*#__PURE__*/_jsx(PropertyListEditor, {
        "data-selenium-test": "edit-columns-modal",
        "data-test-id": "edit-columns-modal",
        isCreateDisabled: false,
        onChange: handleUpdateSelectedColumns,
        onRequiredChange: handleUpdateSelectedColumns,
        propertyCategories: propertyCategories,
        type: DEAL,
        use: COLUMN_SETTINGS,
        value: selectedColumns || getSelectedColumns(columns, properties, selectedForecastTypeAmountProperty)
      })
    }), /*#__PURE__*/_jsxs(UIDialogFooter, {
      children: [/*#__PURE__*/_jsx(UIButton, {
        "data-selenium-test": "configure-columns-save-button",
        "data-test-id": "configure-columns-save-button",
        disabled: isSaveColumnsDisabled,
        use: "primary",
        onClick: handleApplySelectedColumns,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "editDefaultColumnsModal.actions.apply"
        })
      }), /*#__PURE__*/_jsx(UIButton, {
        "data-selenium-test": "configure-columns-cancel-button",
        onClick: onClose,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "editDefaultColumnsModal.actions.cancel"
        })
      })]
    })]
  });
};
export default EditDefaultColumnsModal;